import { useEffect, useCallback, useImperativeHandle } from "react";
import { useAppDispatch } from "@lib/stores";
import { db } from "@modules/local/db";
import { EmptyParameters, DataResult, ContentPageRefType } from "../types";
import { useContentEntityInfoLiveQuery } from "./useContentEntityInfoLiveQuery";
import { useContentEntityLiveQuery } from "./useContentEntityLiveQuery";
import * as Api from "@modules/api";
import {
  getUniqueId,
  getContent,
  processParameterValue,
  getMoreContent,
  defaultPageSize,
} from "../helpers/content";
import { useAppLiveQuery } from "@modules/local/hooks";
import { cacheExpired } from "@modules/local/helpers";
import { CollectionComponentsActions } from "@lib/fetchers/collectionComponents";
import { useLoader } from "@utils/hooks/useLoader";

export type PageDataResult<TParameters = EmptyParameters> =
  DataResult<TParameters> & {
    pageData: Api.Server.CollectionComponentDetailsOutput | null;
  };

export type UsePageDataArgs = {
  pageId: string;
  context?: Api.Server.ContentEntityOutput;
  onItemPress?: (item: Api.Server.ContentEntityOutput) => void | Promise<void>;
  pageNumber?: number;
  pageSize?: number;
  criteria?: string;
};

export function usePageData<TParameters = EmptyParameters>(
  { pageId, context, pageNumber = 1, pageSize = 10, criteria }: UsePageDataArgs,
  ref?: React.ForwardedRef<ContentPageRefType>
): PageDataResult<TParameters> {
  const dispatch = useAppDispatch();

  const { isLoading, startFunction } = useLoader();

  const { result: pageDataResult, isLoading: isPagesDBLoading } =
    useAppLiveQuery(() => db.pages.get(pageId), [pageId]);

  const pageData = pageDataResult?.id === pageId ? pageDataResult : undefined;

  const uniqueId = getUniqueId(
    "page",
    pageId,
    context?.id,
    context?.contentEntityTypeId,
    criteria
  );

  const { result: contentsResult, isLoading: isContentEntityDBLoading } =
    useContentEntityLiveQuery(uniqueId, pageSize ?? defaultPageSize);

  const { result: contentInfo, isLoading: isContentEntityInfoLoading } =
    useContentEntityInfoLiveQuery(uniqueId);

  const contents =
    contentsResult?.id === uniqueId ? contentsResult.data : undefined;

  const getPageAsync = async (pageId: string, forceFetch: boolean = false) => {
    if (!pageId) return;

    if (!forceFetch && !cacheExpired(pageData)) return;

    await CollectionComponentsActions.getCollectionComponentByIdAsync(pageId);
  };

  useEffect(() => {
    if (isPagesDBLoading) return;

    startFunction(async () => {
      await getPageAsync(pageId);
    });
  }, [pageId, isPagesDBLoading]);

  useEffect(() => {
    if (pageData) {
      startFunction(async () => {
        await getContent({
          context,
          dispatch,
          selectorsData: pageData,
          uniqueId,
          pageNumber,
          pageSize,
          criteria,
        });
      });
    }
  }, [
    JSON.stringify(pageData?.selectors),
    JSON.stringify(pageData?.sorters),
    context?.id,
    context?.contentItemType,
    pageNumber,
    pageSize,
    criteria,
  ]);

  const content = contents?.[0];

  const getParameterValues = useCallback(
    <TKey extends keyof TParameters>(
      keys: TKey[]
    ): {
      [K in TKey]?: TParameters[K];
    } => {
      if (pageData === undefined) {
        return {};
      }
      return keys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: processParameterValue<TParameters, TKey>(
            key,
            pageData,
            content
          ),
        }),
        {} as { [K in TKey]: TParameters[K] }
      );
    },
    [
      pageData,
      content,
      // content?.parameters?.map(p => p.value)
    ]
  );

  const refresh = useCallback(async () => {
    await startFunction(async () => {
      await getPageAsync(pageId, true);
      await getContent({
        context,
        dispatch,
        selectorsData: pageData,
        uniqueId,
        pageNumber,
        pageSize,
        criteria,
      });
    });
  }, [
    pageId,
    JSON.stringify(pageData?.selectors),
    JSON.stringify(pageData?.sorters),
    context?.id,
    context?.contentItemType,
    pageNumber,
    pageSize,
    criteria,
  ]);

  const getMore = useCallback(async () => {
    await getMoreContent({
      context,
      dispatch,
      selectorsData: pageData,
      uniqueId,
    });
  }, [
    JSON.stringify(pageData?.selectors),
    JSON.stringify(pageData?.sorters),
    context?.id,
    context?.contentItemType,
  ]);

  const result: PageDataResult<TParameters> = {
    pageData: pageData ?? null,
    content,
    contents: contents ?? [],
    meta: contentInfo ?? {},
    getParameterValues,
    // TODO: Check these
    isLoading: isLoading,
    isLoadingMore: contentInfo?.uniqueId === "loading",
    refresh,
    uniqueId,
    getMore,
  };

  useImperativeHandle(ref, () => result);

  return result;
}
